// Copyright © 2019-2026 Focal Systems. All rights reserved.

import type { FieldType } from "@/features/tasks/types";

/**
 * A stand-in task the card preview renders against.
 *
 * Every field path the shipped appearances reference — `item.*`, `custom.*`, `image.*`,
 * `detectedAt` — resolves to something here, so a preview shows real-looking values instead of
 * a grid of dashes. It mirrors the fixture Impact's own card editor uses.
 */
export const SAMPLE_TASK: Record<string, unknown> = {
  id: 1,
  type: "custom",
  detectedAt: "2026-08-30T07:12:00",
  durationInMinutes: 78,
  value: 123,
  expectedValue: 5.88,
  isWorkable: true,
  image: {
    url: null,
    image_taken_at: "2026-08-30T06:58:12",
  },
  item: {
    name: "Semi Skimmed Milk 2L",
    upc: "5000112637922",
    itemNumber: "420013214",
    mod: "4",
    inventoryLevel: 12,
    aisle: "GR01",
    aisleSide: "L",
    brand: "Focal Dairy",
    capacity: 5,
    casePackSize: 6,
    daysOut: 2,
    department: "DSD DAIRY (42)",
    itemSize: "3.5 OZ",
    lastReceivedAt: "2026-08-28T00:00:00",
    position: "1",
    shelf: "4",
    shelfTagName: "GR-01-L-4",
  },
  location: {
    primary: { name: "Primary Location", coords: [1938, 866] },
    current: { name: "GR-01-L-4", status: "oos" },
  },
  // A few appearances index into the rendered tab payload rather than the task itself
  tabs: {
    issues: { slidesCount: 2 },
    customIssues: [
      { fields: [{ value: "—" }, { value: "—" }, { value: "2" }] },
      { fields: [{ value: "—" }] },
      { fields: [{ value: "1" }, { value: "3" }] },
    ],
  },
  // Every `custom.*` path the shipped appearances reference resolves here, so a preview shows
  // numbers where the handheld shows numbers instead of a grid of dashes.
  custom: {
    capacity: 5,
    fill_rate: 0.42,
    restock_threshold: 3,
    departments: "Dairy, Chilled",
    boh_ingredients: { cases: 2, units: 4 },
    case_capacity_operands: { cases: 1, units: 6 },
    compliance_score: 0.87,
    osa_score: 0.94,
    detected_shelves: 6,
    shelf_gap: 2,
    mod_count: 3,
    extra_tags: 0,
    missing_tags: 0,
    missing_price: 0,
    incorrect_price: 1,
    imported_pog: { start_date: "2026-08-24" },
    zap_the_gap: { tag_name: "GR-01-L-4" },
    rotation: {
      schedule_name: "Weekly Chiller Rotation",
      schedule_type: "weekly",
      rotation_start_date: "2026-08-24",
      rotation_end_date: "2026-08-31",
      tag_names: "GR-01-L-4, GR-01-L-5",
      number_of_products: 18,
      number_of_mods: 3,
    },
  },
};

/** Read a dotted path (`item.shelf`, `custom.rotation.tag_names`) out of an object. */
export function resolveFieldPath(source: unknown, path: string | null | undefined): unknown {
  if (!path) return undefined;
  let current = source;
  for (const part of path.split(".")) {
    if (current && typeof current === "object" && part in (current as Record<string, unknown>)) {
      current = (current as Record<string, unknown>)[part];
    } else {
      return undefined;
    }
  }
  return current;
}

/**
 * Fill a `{{path}}` template the way the Action Tool's i18next interpolator does.
 *
 * An unresolved placeholder keeps its braces so the editor shows the author that the path is
 * wrong, rather than quietly rendering an empty header.
 */
export function interpolateTemplate(template: string, source: unknown = SAMPLE_TASK): string {
  return template.replace(/\{\{\s*([^}\s]+)\s*\}\}/g, (match, path: string) => {
    const value = resolveFieldPath(source, path);
    return value == null ? match : String(value);
  });
}

/** Render a field's raw value the way its `type` says the Action Tool would. */
export function formatFieldValue(type: FieldType, value: unknown): string {
  if (value == null) return "—";
  if (typeof value === "object") {
    const record = value as Record<string, unknown>;
    // The case/unit types carry an operand pair rather than a scalar
    if ("cases" in record || "units" in record) {
      const cases = record.cases ?? 0;
      const units = record.units ?? 0;
      return `${cases} cs / ${units} un`;
    }
    return JSON.stringify(value);
  }

  const numeric = typeof value === "number" ? value : Number(value);
  const hasNumber = !Number.isNaN(numeric);

  switch (type) {
    case "int":
      return hasNumber ? String(Math.round(numeric)) : String(value);
    case "float1":
      return hasNumber ? numeric.toFixed(1) : String(value);
    case "float2":
    case "currency2":
      return hasNumber ? numeric.toFixed(2) : String(value);
    case "float":
    case "currency":
      return hasNumber ? String(numeric) : String(value);
    case "percent":
      return hasNumber ? `${Math.round(numeric * 100)}%` : String(value);
    case "percent1":
      return hasNumber ? `${(numeric * 100).toFixed(1)}%` : String(value);
    case "percent2":
      return hasNumber ? `${(numeric * 100).toFixed(2)}%` : String(value);
    case "percent100":
      return hasNumber ? `${Math.round(numeric)}%` : String(value);
    case "duration":
      return hasNumber ? `${Math.floor(numeric / 60)}h ${numeric % 60}m` : String(value);
    case "units":
      return `${value} un`;
    case "cases":
      return `${value} cs`;
    case "caseCapacity":
    case "caseUnits":
    case "caseCapacityCaseOrUnits":
      return `${value}`;
    case "space":
      return "";
    default:
      return String(value);
  }
}

/**
 * Whether a field is rendered for the previewed chain.
 *
 * Mirrors the filter in `TaskCardHeaderDetails` exactly: an unguarded field always shows; a list
 * where *every* entry is `!`-prefixed is an exclusion list; any other list is an allow-list. Note
 * that a guarded field shows for no chain at all — the handheld always knows which chain it is in,
 * so a field guarded for `asda` is simply absent everywhere else, including when the back-office
 * preview has no chain selected.
 */
export function fieldAppliesToChain(chains: string[] | undefined, chainName: string | undefined): boolean {
  if (!chains?.length) return true;
  if (chains.every((chain) => chain.startsWith("!"))) {
    const excluded = chains.map((chain) => chain.slice(1));
    return !!chainName && !excluded.includes(chainName);
  }
  return !!chainName && chains.includes(chainName);
}

/**
 * Every chain named by a guard on this card's fields, `!` stripped and de-duplicated.
 *
 * Drives the preview's chain switcher: these are the only chains for which the card renders
 * differently, so they are the only ones worth offering.
 */
export function referencedChains(fieldLists: { chains?: string[] }[][]): string[] {
  const names = new Set<string>();
  for (const list of fieldLists) {
    for (const field of list) {
      for (const chain of field.chains ?? []) {
        names.add(chain.startsWith("!") ? chain.slice(1) : chain);
      }
    }
  }
  return [...names].sort();
}
